import {
  Button,
  Card,
  SelectField,
  TextInputField,
  toaster,
} from 'evergreen-ui';
import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import './NewRound.css';
import buildMatches from './buildMatches';
import { useNavigate, useParams } from 'react-router-dom';
import PlayerDropdown from './PlayerDropdown';
import useStore from './useStore';

const NewRound: FunctionComponent = () => {
  const { categoryId } = useParams();
  const [roundName, setRoundName] = useState('');
  const [type, setType] = useState('doubles');
  const [tourney] = useState('');
  const [numberOfPlayers, setNumberOfPlayers] = useState(0);
  const [numberOfGroups, setNumberOfGroups] = useState(0);
  const [playTill, setPlayTill] = useState(0);
  const [winBy, setWinBy] = useState(0);
  const [, setShowPlayers] = useState(false);

  const [roundPlayers, setRoundPlayers] = useState<number[]>([]);
  const navigate = useNavigate();
  const totalRoundPlayers = roundPlayers.reduce((prev, acc) => prev + acc, 0);

  const [players, setPlayers] = useState<number[]>([]);

  // Get data from store
  const rounds = useStore((state) => state.rounds);
  const categories = useStore((state) => state.categories);
  const playerCategories = useStore((state) => state.playerCategories);

  // Check if this is the first round for this category
  const isFirstRound = useMemo(() => {
    const categoryRounds = rounds.filter(
      (r) => r.categoryId === Number(categoryId)
    );
    return categoryRounds.length === 0;
  }, [rounds, categoryId]);

  // Get category details
  const category = useMemo(() => {
    return categories.find((c) => c.id === Number(categoryId));
  }, [categories, categoryId]);

  // Get seeded players for this category (sorted by seed)
  const seededPlayers = useMemo(() => {
    if (!isFirstRound) return [];
    return playerCategories
      .filter((pc) => pc.categoryId === Number(categoryId) && pc.seed !== null)
      .sort((a, b) => (a.seed as number) - (b.seed as number))
      .map((pc) => pc.playerId);
  }, [playerCategories, categoryId, isFirstRound]);

  // Prefill values for first round
  useEffect(() => {
    if (isFirstRound) {
      setRoundName('Qualifiers');
      if (category?.numPlayers) {
        setNumberOfPlayers(category.numPlayers);
      }
      if (seededPlayers.length > 0) {
        setPlayers(seededPlayers);
      }
    }
  }, [isFirstRound, category, seededPlayers]);

  const handleConfirm = useCallback(async () => {
    try {
      await buildMatches({
        numberOfGroups,
        numberOfPlayers,
        roundPlayers,
        playerIds: players,
        type,
        roundName,
        tourney,
        playTill,
        winBy,
        categoryId: Number(categoryId),
      });
      toaster.success('New round has been created');
      navigate('/');
    } catch {
      toaster.danger('Error creating new round');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    numberOfGroups,
    numberOfPlayers,
    roundPlayers,
    players,
    type,
    roundName,
  ]);

  const DialogContent = (
    <>
      <TextInputField
        required
        label='Round name'
        name='round-name'
        value={roundName}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setShowPlayers(false);
          setRoundName(e.target.value);
        }}
      />

      <SelectField
        label='Type'
        required
        value={type}
        onChange={(e) => {
          setType(e.target.value);
          if (e.target.value === 'semis') {
            setNumberOfGroups(1);
            setNumberOfPlayers(4);
          }
        }}
      >
        <option value='doubles' selected>
          Doubles
        </option>
        <option value='singles'>Singles</option>
        <option value='teams'>Teams</option>
        <option value='semis'>Semi's</option>
      </SelectField>

      <TextInputField
        required
        label='Number of players'
        name='number-of-players'
        value={numberOfPlayers}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setShowPlayers(false);
          setNumberOfPlayers(Number(e.target.value));
        }}
      />

      <TextInputField
        required
        label='Number of groups '
        name='number-of-groups'
        value={numberOfGroups}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setShowPlayers(false);
          setNumberOfGroups(Number(e.target.value));
        }}
      />

      <TextInputField
        required
        label='Play Till'
        name='play-till'
        value={playTill}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setShowPlayers(false);
          setPlayTill(Number(e.target.value));
        }}
      />
      <TextInputField
        required
        label='Win By'
        name='win-by'
        value={winBy}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setShowPlayers(false);
          setWinBy(Number(e.target.value));
        }}
      />

      <div className='groupPlayerContainer'>
        {numberOfGroups > 0 &&
          Array(numberOfGroups)
            .fill('')
            .map((_, index) => (
              <TextInputField
                label={`Group ${index + 1}`}
                style={{ width: '30%' }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setRoundPlayers((prev) => {
                    const newArray = [...prev];
                    newArray[index] = Number(e.target.value);
                    return newArray;
                  })
                }
                value={roundPlayers[index]}
              ></TextInputField>
            ))}
      </div>

      <Button
        appearance='primary'
        onClick={() => {
          setShowPlayers(true);
          if (!isFirstRound) {
            setPlayers([]);
          }
        }}
        disabled={
          !roundName ||
          !numberOfPlayers ||
          !numberOfGroups ||
          totalRoundPlayers !== numberOfPlayers ||
          roundPlayers.length !== numberOfGroups
        }
      >
        Ready to add players
      </Button>
    </>
  );

  const PlayerContent = (
    <>
      <div className='groupPlayerContainer'>
        {Array(numberOfPlayers)
          .fill('')
          .map((_, index) => {
            return (
              <PlayerDropdown
                key={index}
                selectedPlayerId={players[index]}
                onChange={(id) => {
                  setPlayers((prev) => {
                    const newArray = [...prev];
                    newArray[index] = id;
                    return newArray;
                  });
                }}
              />
            );
          })}
      </div>
      <Button
        appearance='primary'
        onClick={handleConfirm}
        disabled={
          !roundName ||
          !numberOfPlayers ||
          !numberOfGroups ||
          totalRoundPlayers !== numberOfPlayers ||
          roundPlayers.length !== numberOfGroups ||
          players.length !== numberOfPlayers
          // ||
          // players.some((p) => p === '')
        }
      >
        Generate Matches
      </Button>
    </>
  );

  return (
    <>
      <h1>New Round</h1>
      <div className='newCategoryContainer'>
        <Card elevation={1} className='card'>
          {DialogContent}
        </Card>
        <Card elevation={1} className='card'>
          {/* {showPlayers && PlayerContent} */}
          {PlayerContent}
        </Card>
      </div>
    </>
  );
};

export default NewRound;
